import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import SEOMeta from '@/components/base/SEOMeta';
import { getMovieById, categoryMap } from '@/mocks/movies';
import { getArticleById, getLatestArticles, getArticlesByCategory } from '@/mocks/articles';
import type { ArticleItem } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  let article: ArticleItem | undefined;
  let relatedArticles: ArticleItem[] = [];

  const movie = id ? getMovieById(id) : undefined;

  if (movie) {
    article = {
      id: movie.id,
      title: movie.title,
      category: movie.categoryLabel,
      categorySlug: movie.category,
      excerpt: movie.description,
      content: `<p>${movie.description}</p><p>类型：${movie.tags.join(' / ')}</p><p>年份：${movie.year} | 地区：${movie.region}</p><p>评分：${movie.rating}</p><p>更新时间：${movie.updateDate}</p>`,
      author: '黄瓜影院编辑部',
      date: movie.updateDate,
      imageSeq: movie.imageSeq,
      tags: movie.tags,
      views: Math.floor(Math.random() * 50000) + 5000,
    };
    relatedArticles = getArticlesByCategory(movie.category).slice(0, 4);
  } else {
    article = id ? getArticleById(id) : undefined;
    if (article) {
      relatedArticles = getLatestArticles(4).filter((a) => a.id !== article!.id).slice(0, 3);
    }
  }

  if (!article) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-2xl text-foreground-500 mb-4">文章不存在</p>
          <Link to="/" className="text-primary-500 hover:text-primary-600 cursor-pointer">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const categoryLabel = categoryMap[article.categorySlug] || article.category;

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://huanggua.tv/' },
      { '@type': 'ListItem', position: 2, name: categoryLabel, item: `https://huanggua.tv/category/${article.categorySlug}` },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.date,
    url: `https://huanggua.tv/article/${article.id}`,
  };

  const imageUrls: Record<string, string> = {
    'movie-cover-01': 'https://readdy.ai/api/search-image?query=Cinematic%20sci-fi%20movie%20scene%20with%20dramatic%20cosmic%20lighting%2C%20epic%20space%20atmosphere%2C%20cinematic%20film%20still%2C%20warm%20orange%20and%20deep%20blue%20tones%2C%20professional%20movie%20photography%2C%20no%20text&width=1200&height=600&seq=art-mc-01&orientation=landscape',
    'movie-cover-02': 'https://readdy.ai/api/search-image?query=Ancient%20Chinese%20poetry%20animated%20film%20scene%2C%20traditional%20ink%20wash%20painting%20style%2C%20Tang%20dynasty%20aesthetic%2C%20warm%20golden%20earth%20tones%2C%20artistic%20composition%2C%20no%20text&width=1200&height=600&seq=art-mc-02&orientation=landscape',
    'movie-cover-03': 'https://readdy.ai/api/search-image?query=Epic%20Chinese%20mythology%20fantasy%20battle%20scene%2C%20ancient%20warriors%2C%20dramatic%20golden%20crimson%20lighting%2C%20grand%20scale%2C%20cinematic%2C%20no%20text&width=1200&height=600&seq=art-mc-03&orientation=landscape',
    'movie-cover-04': 'https://readdy.ai/api/search-image?query=War%20film%20battlefield%20scene%20with%20soldiers%2C%20dramatic%20smoky%20atmosphere%2C%20cold%20winter%20tones%2C%20cinematic%20composition%2C%20historical%20war%20photography%2C%20no%20text&width=1200&height=600&seq=art-mc-04&orientation=landscape',
    'movie-cover-05': 'https://readdy.ai/api/search-image?query=Underwater%20fantasy%20animated%20scene%20with%20colorful%20particle%20effects%2C%20magical%20deep%20sea%20atmosphere%2C%20vibrant%20blue%20and%20pink%20tones%2C%20ethereal%20lighting%2C%20no%20text&width=1200&height=600&seq=art-mc-05&orientation=landscape',
    'movie-cover-06': 'https://readdy.ai/api/search-image?query=Military%20action%20film%20scene%20with%20naval%20soldiers%20in%20desert%20environment%2C%20intense%20combat%20atmosphere%2C%20dusty%20warm%20tones%2C%20cinematic%20photography%2C%20no%20text&width=1200&height=600&seq=art-mc-06&orientation=landscape',
    'tv-cover-01': 'https://readdy.ai/api/search-image?query=Chinese%20historical%20palace%20drama%20scene%2C%20ornate%20costumes%2C%20golden%20lantern%20light%2C%20royal%20court%20interior%2C%20rich%20red%20and%20gold%20tones%2C%20cinematic%2C%20no%20text&width=1200&height=600&seq=art-tv-01&orientation=landscape',
    'tv-cover-02': 'https://readdy.ai/api/search-image?query=Modern%20crime%20drama%20city%20night%20scene%2C%20police%20investigation%20atmosphere%2C%20dark%20blue%20noir%20lighting%2C%20urban%20street%2C%20cinematic%20composition%2C%20no%20text&width=1200&height=600&seq=art-tv-02&orientation=landscape',
    'tv-cover-03': 'https://readdy.ai/api/search-image?query=Sci-fi%20laboratory%20scene%20with%20holographic%20displays%2C%20futuristic%20technology%2C%20cool%20blue%20lighting%2C%20mysterious%20atmosphere%2C%20cinematic%20composition%2C%20no%20text&width=1200&height=600&seq=art-tv-03&orientation=landscape',
    'tv-cover-04': 'https://readdy.ai/api/search-image?query=1990s%20Shanghai%20cityscape%20at%20night%2C%20neon%20lights%20and%20vintage%20atmosphere%2C%20warm%20golden%20tones%2C%20nostalgic%20urban%20scene%2C%20cinematic%20composition%2C%20no%20text&width=1200&height=600&seq=art-tv-04&orientation=landscape',
    'tv-cover-05': 'https://readdy.ai/api/search-image?query=Northeast%20China%20small%20town%20in%20winter%2C%20snowy%20streets%2C%20old%20factory%20buildings%2C%20nostalgic%20atmosphere%2C%20muted%20cold%20tones%2C%20cinematic%20photography%2C%20no%20text&width=1200&height=600&seq=art-tv-05&orientation=landscape',
    'tv-cover-06': 'https://readdy.ai/api/search-image?query=Ancient%20Chinese%20fantasy%20romance%20scene%2C%20ethereal%20costumes%2C%20magical%20forest%20atmosphere%2C%20soft%20dreamy%20lighting%2C%20cinematic%20composition%2C%20no%20text&width=1200&height=600&seq=art-tv-06&orientation=landscape',
    'variety-cover-01': 'https://readdy.ai/api/search-image?query=Bright%20colorful%20outdoor%20variety%20show%20set%20with%20running%20game%20props%2C%20sunny%20day%20vibrant%20atmosphere%2C%20energetic%20composition%2C%20no%20text&width=1200&height=600&seq=art-var-01&orientation=landscape',
    'variety-cover-02': 'https://readdy.ai/api/search-image?query=Peaceful%20rural%20Chinese%20farmhouse%20with%20vegetable%20garden%2C%20warm%20sunset%20lighting%2C%20cozy%20countryside%20atmosphere%2C%20no%20text&width=1200&height=600&seq=art-var-02&orientation=landscape',
    'variety-cover-03': 'https://readdy.ai/api/search-image?query=Music%20concert%20stage%20with%20dramatic%20spotlights%20and%20microphone%20stand%2C%20professional%20performance%20atmosphere%2C%20dark%20theater%2C%20cinematic%2C%20no%20text&width=1200&height=600&seq=art-var-03&orientation=landscape',
    'anime-cover-01': 'https://readdy.ai/api/search-image?query=Chinese%20cultivation%20fantasy%20landscape%20with%20floating%20mountains%20and%20mist%2C%20ethereal%20jade%20green%20and%20golden%20lighting%2C%20epic%20scale%2C%20no%20text&width=1200&height=600&seq=art-anm-01&orientation=landscape',
    'anime-cover-02': 'https://readdy.ai/api/search-image?query=Fantasy%20martial%20arts%20battle%20scene%20with%20fire%20and%20energy%20effects%2C%20dramatic%20red%20and%20orange%20lighting%2C%20epic%20scale%2C%20no%20text&width=1200&height=600&seq=art-anm-02&orientation=landscape',
    'anime-cover-03': 'https://readdy.ai/api/search-image?query=Epic%20fantasy%20world%20landscape%20with%20ancient%20ruins%20and%20magical%20energy%2C%20vast%20scale%2C%20golden%20and%20crimson%20tones%2C%20cinematic%2C%20no%20text&width=1200&height=600&seq=art-anm-03&orientation=landscape',
    'anime-cover-04': 'https://readdy.ai/api/search-image?query=Dark%20cultivation%20fantasy%20scene%20with%20lonely%20warrior%20on%20mountain%20peak%2C%20mysterious%20purple%20and%20black%20lighting%2C%20atmospheric%2C%20no%20text&width=1200&height=600&seq=art-anm-04&orientation=landscape',
    'article-cover-01': 'https://readdy.ai/api/search-image?query=Cinematic%20movie%20posters%20collage%20on%20a%20dark%20wall%2C%20warm%20spotlight%20illumination%2C%20film%20reel%20decorations%2C%20cozy%20cinema%20review%20blog%20atmosphere%2C%20rich%20dark%20red%20and%20golden%20accents%2C%20editorial%20photography%2C%20no%20text&width=1200&height=600&seq=art-art-01&orientation=landscape',
    'article-cover-02': 'https://readdy.ai/api/search-image?query=Ancient%20Chinese%20palace%20court%20scene%20with%20ornate%20robes%20and%20golden%20decor%2C%20dramatic%20lighting%20through%20paper%20windows%2C%20historical%20drama%20aesthetic%2C%20rich%20red%20and%20gold%20tones%2C%20cinematic%20composition%2C%20no%20text&width=1200&height=600&seq=art-art-02&orientation=landscape',
    'article-cover-03': 'https://readdy.ai/api/search-image?query=Chinese%20animation%20art%20studio%20with%20character%20designs%20and%20storyboards%20on%20walls%2C%20warm%20creative%20workspace%2C%20ink%20paintings%20and%20digital%20screens%2C%20artistic%20atmosphere%2C%20soft%20natural%20light%2C%20no%20text&width=1200&height=600&seq=art-art-03&orientation=landscape',
    'article-cover-04': 'https://readdy.ai/api/search-image?query=Colorful%20TV%20variety%20show%20studio%20set%20with%20bright%20lights%20and%20game%20props%2C%20energetic%20atmosphere%2C%20vibrant%20coral%20and%20yellow%20lighting%2C%20professional%20studio%20photography%2C%20dynamic%20composition%2C%20no%20text&width=1200&height=600&seq=art-art-04&orientation=landscape',
    'article-cover-05': 'https://readdy.ai/api/search-image?query=Deep%20space%20cosmic%20scene%20with%20distant%20galaxies%20and%20nebula%2C%20sci-fi%20mysterious%20atmosphere%2C%20dark%20blue%20and%20purple%20cosmic%20lighting%2C%20vast%20universe%20scale%2C%20minimalist%20space%20composition%2C%20no%20text%2C%20no%20planets&width=1200&height=600&seq=art-art-05&orientation=landscape',
    'article-cover-06': 'https://readdy.ai/api/search-image?query=Warm%20family%20movie%20night%20scene%20with%20popcorn%20and%20cozy%20living%20room%20atmosphere%2C%20soft%20warm%20golden%20lighting%2C%20comfortable%20sofa%20with%20blankets%2C%20home%20cinema%20vibe%2C%20editorial%20lifestyle%20photography%2C%20no%20text&width=1200&height=600&seq=art-art-06&orientation=landscape',
  };

  return (
    <>
      <SEOMeta
        title={article.title}
        description={article.excerpt}
        keywords={`${article.title},${article.tags.join(',')},黄瓜影院`}
        canonicalUrl={`https://huanggua.tv/article/${article.id}`}
        ogTitle={article.title}
        ogDescription={article.excerpt}
        ogType="article"
        ogImage={imageUrls[article.imageSeq] || imageUrls['movie-cover-01']}
        schema={{ ...breadcrumbSchema, ...articleSchema }}
      />
      <div className="pt-20 pb-12 md:pb-16">
        <div className="max-w-[900px] mx-auto px-4 md:px-6">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-500 transition-colors duration-200 whitespace-nowrap cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link to={`/category/${article.categorySlug}`} className="hover:text-primary-500 transition-colors duration-200 whitespace-nowrap cursor-pointer">
              {categoryLabel}
            </Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-900 font-medium line-clamp-1">{article.title}</span>
          </nav>

          <article>
            <header className="mb-8">
              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.views.toLocaleString()} 阅读
                </span>
                <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="px-2.5 py-0.5 bg-accent-100 text-accent-700 text-xs font-medium rounded-md hover:bg-accent-200 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  {article.category}
                </Link>
              </div>
            </header>

            <div className="rounded-xl overflow-hidden mb-8 bg-background-200">
              <img
                src={imageUrls[article.imageSeq] || imageUrls['movie-cover-01']}
                alt={article.title}
                className="w-full aspect-[2/1] object-cover"
              />
            </div>

            <div
              className="prose prose-lg max-w-none text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-8 pt-6 border-t border-background-200">
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="mt-12 pt-8 border-t border-background-200">
              <h2 className="font-heading text-xl font-bold text-foreground-950 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group bg-background-100 rounded-xl p-4 hover:bg-background-200/70 transition-all duration-200 cursor-pointer"
                  >
                    <h3 className="font-heading text-sm font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200">
                      {ra.title}
                    </h3>
                    <p className="text-xs text-foreground-500 line-clamp-2 mb-2">
                      {ra.excerpt}
                    </p>
                    <span className="text-xs text-foreground-400">{ra.date}</span>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </>
  );
}